import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  People,
  Assignment,
  Payment,
  Analytics,
  Add,
  Block,
  CheckCircle,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';

interface AdminStats {
  totalUsers: number;
  totalApplications: number;
  totalRevenue: number;
  pendingApplications: number;
  approvedApplications: number;
  rejectedApplications: number;
  monthlyGrowth: string;
  activeAgents: number;
  corporateClients: number;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  companyName?: string;
  createdAt: string;
  apiKey?: string;
  rateLimit?: number;
}

interface Application {
  id: string;
  applicantName: string;
  destination: string;
  visaType: string;
  status: string;
  submittedAt: string;
  processingTime: string;
}

interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  method: string;
  description: string;
  processedAt: string;
}

const AdminDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalApplications: 0,
    totalRevenue: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    monthlyGrowth: '0%',
    activeAgents: 0,
    corporateClients: 0,
  });
  
  const [users, setUsers] = useState<User[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'general',
    companyName: '',
    status: 'active',
    tempPassword: '',
  });

  // Fetch admin dashboard data
  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch analytics stats
      const statsResponse = await fetch(`${API_BASE_URL}/analytics/stats`, { headers });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(prev => ({
          ...prev,
          totalUsers: statsData.data.totalUsers,
          totalApplications: statsData.data.totalApplications,
          totalRevenue: statsData.data.totalRevenue,
          pendingApplications: statsData.data.pendingApplications,
          approvedApplications: statsData.data.approvedApplications,
          rejectedApplications: statsData.data.rejectedApplications,
          monthlyGrowth: statsData.data.monthlyGrowth,
        }));
      }

      // Fetch users list and compute real role-based counts
      const usersResponse = await fetch(`${API_BASE_URL}/users/list`, { headers });
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        const usersList = usersData.data || [];
        setUsers(usersList);

        // Compute real agent and corporate counts from user data
        const agentCount = usersList.filter((u: User) => u.role === 'agent' && u.status === 'active').length;
        const corporateCount = usersList.filter((u: User) => u.role === 'corporate').length;
        setStats(prev => ({
          ...prev,
          activeAgents: agentCount,
          corporateClients: corporateCount,
        }));
      }

      // Fetch applications
      const appsResponse = await fetch(`${API_BASE_URL}/visa-applications/list`, { headers });
      if (appsResponse.ok) {
        const appsData = await appsResponse.json();
        setApplications(appsData.data || []);
      }

      // Fetch payments
      const paymentsResponse = await fetch(`${API_BASE_URL}/payments/list`, { headers });
      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        setPayments(paymentsData.data || []);
      }

    } catch (error) {
      console.error('Error fetching admin data:', error);
      showError('Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Create new user
  const handleCreateUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...userFormData,
          password: userFormData.tempPassword || (Math.random().toString(36).slice(-10) + 'A1!'),
          fullName: `${userFormData.firstName} ${userFormData.lastName}`
        })
      });

      if (response.ok) {
        showSuccess('User created successfully. A temporary password has been set.');
        setUserModalOpen(false);
        setUserFormData({
          email: '',
          firstName: '',
          lastName: '',
          role: 'general',
          companyName: '',
          status: 'active',
          tempPassword: '',
        });
        fetchAdminData(); // Refresh data
      } else {
        const error = await response.json();
        showError(error.message || 'Failed to create user');
      }
    } catch (error) {
      showError('Error creating user');
    }
  };

  // Update user status
  const handleUpdateUserStatus = async (userId: string, newStatus: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        showSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        fetchAdminData();
      } else {
        showError('Failed to update user status');
      }
    } catch (error) {
      showError('Error updating user status');
    }
  };

  // Update application status
  const handleUpdateApplicationStatus = async (appId: string, newStatus: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/visa-applications/${appId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        showSuccess('Application status updated successfully');
        fetchAdminData();
      } else {
        showError('Failed to update application status');
      }
    } catch (error) {
      showError('Error updating application status');
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminData();
    }
  }, [user, fetchAdminData]);

  if (user?.role !== 'admin') {
    return (
      <Container>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ---- Hero ---- */}
      <Box
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          color: '#fff',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 3,
          mb: 4,
          px: 4,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
            System overview, user management, and platform health monitoring.
          </Typography>
        </Box>
      </Box>
      
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.totalUsers}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Users
                  </Typography>
                </Box>
                <People sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #29B848 0%, #4CAF50 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.totalApplications}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Applications
                  </Typography>
                </Box>
                <Assignment sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    ${stats.totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Revenue
                  </Typography>
                </Box>
                <Payment sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.monthlyGrowth}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Monthly Growth
                  </Typography>
                </Box>
                <TrendingUp sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Paper elevation={0} sx={{ mb: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="User Management" icon={<People />} />
          <Tab label="Applications" icon={<Assignment />} />
          <Tab label="Payments" icon={<Payment />} />
          <Tab label="Analytics" icon={<Analytics />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">User Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setUserModalOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)' }}
            >
              Create User
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: '#1273E5' }}>
                          {user.firstName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {user.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role} 
                        color={user.role === 'admin' ? 'error' : user.role === 'corporate' ? 'warning' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.companyName || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.status} 
                        color={user.status === 'active' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleUpdateUserStatus(user.id, user.status === 'active' ? 'inactive' : 'active')}
                        color={user.status === 'active' ? 'error' : 'success'}
                      >
                        {user.status === 'active' ? <Block /> : <CheckCircle />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {currentTab === 1 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Typography variant="h6" mb={3}>Application Management</Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Visa Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Processing Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>{app.applicantName}</TableCell>
                    <TableCell>{app.destination}</TableCell>
                    <TableCell>{app.visaType}</TableCell>
                    <TableCell>
                      <Chip 
                        label={app.status} 
                        color={app.status === 'approved' ? 'success' : app.status === 'rejected' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(app.submittedAt).toLocaleDateString()}</TableCell>
                    <TableCell>{app.processingTime}</TableCell>
                    <TableCell>
                      <Select
                        value={app.status}
                        onChange={(e) => handleUpdateApplicationStatus(app.id, e.target.value)}
                        size="small"
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="under_review">Under Review</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {currentTab === 2 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Typography variant="h6" mb={3}>Payment Management</Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Payment ID</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Processed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.id}</TableCell>
                    <TableCell>${payment.amount.toFixed(2)}</TableCell>
                    <TableCell>{payment.currency}</TableCell>
                    <TableCell>
                      <Chip 
                        label={payment.status} 
                        color={payment.status === 'completed' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{payment.method}</TableCell>
                    <TableCell>{payment.description}</TableCell>
                    <TableCell>{new Date(payment.processedAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
              <Typography variant="h6" mb={2}>Application Statistics</Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Approved Applications</Typography>
                <Typography variant="h4" color="success.main">{stats.approvedApplications}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Pending Applications</Typography>
                <Typography variant="h4" color="warning.main">{stats.pendingApplications}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Rejected Applications</Typography>
                <Typography variant="h4" color="error.main">{stats.rejectedApplications}</Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
              <Typography variant="h6" mb={2}>System Overview</Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Active Agents</Typography>
                <Typography variant="h4" color="primary.main">{stats.activeAgents}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Corporate Clients</Typography>
                <Typography variant="h4" color="secondary.main">{stats.corporateClients}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Monthly Growth</Typography>
                <Typography variant="h4" color="success.main">{stats.monthlyGrowth}</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Create User Modal */}
      <Dialog open={userModalOpen} onClose={() => setUserModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={userFormData.firstName}
                onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={userFormData.lastName}
                onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="corporate">Corporate</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={userFormData.companyName}
                onChange={(e) => setUserFormData({ ...userFormData, companyName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Temporary Password"
                type="password"
                placeholder="Leave blank to auto-generate"
                value={userFormData.tempPassword}
                onChange={(e) => setUserFormData({ ...userFormData, tempPassword: e.target.value })}
                helperText="User will be prompted to change this on first login"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserModalOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
